import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Input,
  Button,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Stack,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  SimpleGrid,
  Image,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Flex,
  Tooltip,
  Tag,
  TagLabel,
  Divider,
  Icon,
  Switch,
  Circle
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiSearch, FiMove, FiDollarSign, FiPackage, FiGrid, FiList, FiGift } from 'react-icons/fi';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const ProductSelector = ({ products = [], onChange }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleSearch = async () => {
    setSearching(true);
    setSearchResults([]);

    try {
      const response = await fetch(
        `${qbndAdmin.root}qbnd/v1/products/search?${new URLSearchParams({
          search: searchTerm,
          per_page: '20',
          page: '1'
        })}`,
        {
          headers: {
            'X-WP-Nonce': qbndAdmin.nonce,
            'Accept': 'application/json'
          }
        }
      );

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to search products');
      }

      setSearchResults(data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        handleSearch();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleAddProduct = (product) => {
    const newProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      isFree: false
    };

    onChange([...products, newProduct]);
  };

  const handleRemoveProduct = (index) => {
    const newProducts = [...products];
    newProducts.splice(index, 1);
    onChange(newProducts);
  };

  const handleToggleFree = (index) => {
    const newProducts = [...products];
    newProducts[index].isFree = !newProducts[index].isFree;
    onChange(newProducts);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newProducts = [...products];
    const [removed] = newProducts.splice(result.source.index, 1);
    newProducts.splice(result.destination.index, 0, removed);
    onChange(newProducts);
  };

  const ProductCard = ({ product, onAdd, isAdded }) => (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-2px)',
        boxShadow: 'md',
        borderColor: 'blue.500'
      }}
    >
      <Image
        src={product.image || 'placeholder-image.jpg'}
        alt={product.name}
        h="200px"
        w="full"
        objectFit="cover"
      />
      <Box p={4}>
        <VStack align="stretch" spacing={2}>
          <Text fontWeight="medium" noOfLines={2}>
            {product.name}
          </Text>
          <HStack justify="space-between">
            <Badge colorScheme={product.isFree ? 'green' : 'blue'} fontSize="sm">
              {product.isFree ? 'FREE' : `$${parseFloat(product.price).toFixed(2)}`}
            </Badge>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<FiPlus />}
              onClick={() => onAdd(product)}
              isDisabled={isAdded}
            >
              {isAdded ? 'Added' : 'Add'}
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );

  // Calculate total value excluding free products
  const calculateTotalValue = (productList) => {
    return productList.reduce((sum, product) => {
      if (!product.isFree) {
        return sum + parseFloat(product.price || 0);
      }
      return sum;
    }, 0);
  };

  const totalValue = calculateTotalValue(products);

  return (
    <Box>
      <Box
        bg={useColorModeValue('blue.50', 'blue.900')}
        p={4}
        borderRadius="xl"
        mb={4}
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Circle size="40px" bg="blue.500" color="white">
              <Icon as={FiPackage} />
            </Circle>
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" color="gray.500">Selected Products</Text>
              <Text fontWeight="bold">
                {products.length} {products.length === 1 ? 'Product' : 'Products'}
                {products.some(p => p.isFree) && (
                  <Text as="span" fontSize="sm" color="green.500" ml={2}>
                    ({products.filter(p => p.isFree).length} free)
                  </Text>
                )}
              </Text>
            </VStack>
          </HStack>

          <VStack align="end" spacing={0}>
            <Text fontSize="sm" color="gray.500">Total Value</Text>
            <Text fontSize="lg" fontWeight="bold" color="blue.500">
              ${calculateTotalValue(products).toFixed(2)}
            </Text>
          </VStack>
        </Flex>
      </Box>

      <Stack spacing={6}>
        {/* Selected Products List */}
        <Box
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="xl"
          overflow="hidden"
        >
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="products">
              {(provided) => (
                <VStack
                  spacing={4}
                  p={4}
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  align="stretch"
                >
                  {products.length === 0 ? (
                    <Box
                      py={8}
                      px={4}
                      textAlign="center"
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={borderColor}
                      borderRadius="lg"
                    >
                      <VStack spacing={3}>
                        <Icon as={FiPackage} boxSize={8} color="blue.500" />
                        <Text color="gray.500">
                          No products added to the bundle yet
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<FiPlus />}
                          onClick={onOpen}
                        >
                          Add Products
                        </Button>
                      </VStack>
                    </Box>
                  ) : (
                    products.map((product, index) => (
                      <Draggable
                        key={product.id}
                        draggableId={`product-${product.id}`}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            bg={bgColor}
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="lg"
                            p={4}
                          >
                            <HStack spacing={4}>
                              <Box
                                {...provided.dragHandleProps}
                                cursor="move"
                                color="gray.500"
                              >
                                <FiMove />
                              </Box>
                              <Image
                                src={product.image || 'placeholder-image.jpg'}
                                alt={product.name}
                                boxSize="50px"
                                objectFit="cover"
                                borderRadius="md"
                              />
                              <VStack flex={1} align="start" spacing={1}>
                                <Text fontWeight="medium">{product.name}</Text>
                                <HStack spacing={3}>
                                  <Badge colorScheme={product.isFree ? 'green' : 'blue'}>
                                    {product.isFree ? 'FREE' : `$${parseFloat(product.price).toFixed(2)}`}
                                  </Badge>
                                  <HStack spacing={2}>
                                    <Icon as={FiGift} color={product.isFree ? 'green.500' : 'gray.400'} />
                                    <Switch
                                      size="sm"
                                      colorScheme="green"
                                      isChecked={product.isFree}
                                      onChange={() => handleToggleFree(index)}
                                    />
                                  </HStack>
                                </HStack>
                              </VStack>
                              <IconButton
                                icon={<FiTrash2 />}
                                variant="ghost"
                                colorScheme="red"
                                size="sm"
                                onClick={() => handleRemoveProduct(index)}
                              />
                            </HStack>
                          </Box>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </VStack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>

        {products.length > 0 && (
          <Button leftIcon={<FiPlus />} onClick={onOpen}>
            Add More Products
          </Button>
        )}
      </Stack>

      {/* Product Search Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>
            <HStack justify="space-between">
              <Text>Add Products to Bundle</Text>
              <HStack spacing={2}>
                <IconButton
                  icon={<FiGrid />}
                  size="sm"
                  variant={viewMode === 'grid' ? 'solid' : 'ghost'}
                  colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                  onClick={() => setViewMode('grid')}
                />
                <IconButton
                  icon={<FiList />}
                  size="sm"
                  variant={viewMode === 'list' ? 'solid' : 'ghost'}
                  colorScheme={viewMode === 'list' ? 'blue' : 'gray'}
                  onClick={() => setViewMode('list')}
                />
              </HStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6}>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray.500" />
                </InputLeftElement>
                <Input
                  placeholder="Search products by name, SKU, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  borderRadius="lg"
                />
              </InputGroup>

              {searching ? (
                <Box py={8} textAlign="center">
                  <Spinner size="xl" color="blue.500" />
                  <Text mt={4} color="gray.500">
                    Searching products...
                  </Text>
                </Box>
              ) : searchResults.length > 0 ? (
                viewMode === 'grid' ? (
                  <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={6}>
                    {searchResults.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onAdd={handleAddProduct}
                        isAdded={products.some(p => p.id === product.id)}
                      />
                    ))}
                  </SimpleGrid>
                ) : (
                  <VStack spacing={4} align="stretch">
                    {searchResults.map((product) => (
                      <Box
                        key={product.id}
                        p={4}
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="lg"
                        bg={bgColor}
                        _hover={{ bg: hoverBg }}
                      >
                        <HStack spacing={4}>
                          <Image
                            src={product.image || 'placeholder-image.jpg'}
                            alt={product.name}
                            boxSize="60px"
                            objectFit="cover"
                            borderRadius="md"
                          />
                          <VStack flex={1} align="start" spacing={1}>
                            <Text fontWeight="medium">{product.name}</Text>
                            <HStack spacing={4}>
                              <Badge colorScheme={product.isFree ? 'green' : 'blue'}>
                                {product.isFree ? 'FREE' : `$${parseFloat(product.price).toFixed(2)}`}
                              </Badge>
                              {product.sku && (
                                <Tag size="sm" colorScheme="gray">
                                  <TagLabel>SKU: {product.sku}</TagLabel>
                                </Tag>
                              )}
                            </HStack>
                          </VStack>
                          <Button
                            leftIcon={<FiPlus />}
                            colorScheme="blue"
                            size="sm"
                            onClick={() => handleAddProduct(product)}
                            isDisabled={products.some(p => p.id === product.id)}
                          >
                            {products.some(p => p.id === product.id) ? 'Added' : 'Add to Bundle'}
                          </Button>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                )
              ) : (
                <Box py={12} textAlign="center">
                  <VStack spacing={4}>
                    <Icon as={FiSearch} boxSize={8} color="gray.400" />
                    <Text color="gray.500">
                      {searchTerm
                        ? 'No products found. Try a different search term.'
                        : 'Search for products to add to your bundle'}
                    </Text>
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProductSelector;